import {
  BillableProduct,
  SubscriptionAddon,
  SubscriptionBundle,
  SubscriptionModule,
} from "@coronet/cui";

// Build the existing `subscriptions.*` i18n key of a cui bundle, module or addon.
const bundleKey = (bundle: SubscriptionBundle) => `subscriptions.bundles.${bundle}`;
const moduleKey = (module: SubscriptionModule) => `subscriptions.modules.${module}`;
const addonKey = (addon: SubscriptionAddon) => `subscriptions.addons.${addon}`;
// Billing-only products have no cui enum; their keys are `subscriptions.billingProducts.*`.
const customKey = (name: string) => `subscriptions.billingProducts.${name}`;

/**
 * i18n key of the display name for every {@link BillableProduct}, resolved with the app's `t`.
 * Reuses the existing `subscriptions.{bundles|modules|addons}.<value>` keys; the 5 products that
 * are named differently in cui point at their cui counterpart, and the 4 custom products use
 * `subscriptions.billingProducts.*`. `BILLING_EXCEPTION_OVERRIDE` is shown as "Custom Product".
 */
export const BILLABLE_PRODUCT_LABEL_KEYS: Record<BillableProduct, string> = {
  [BillableProduct.CORO_AI_COMPLETE]: bundleKey(SubscriptionBundle.CORO_AI_COMPLETE),
  [BillableProduct.CORO_AI_ENDPOINT]: bundleKey(SubscriptionBundle.CORO_AI_ENDPOINT),
  [BillableProduct.CORO_AI_ESSENTIALS]: bundleKey(SubscriptionBundle.CORO_AI_ESSENTIALS),
  [BillableProduct.CORO_AI_LITE]: bundleKey(SubscriptionBundle.CORO_AI_LITE),
  [BillableProduct.MANAGED_CORO_AI_COMPLETE]: bundleKey(
    SubscriptionBundle.MANAGED_CORO_AI_COMPLETE
  ),
  [BillableProduct.MANAGED_CORO_AI_ENDPOINT]: bundleKey(
    SubscriptionBundle.MANAGED_CORO_AI_ENDPOINT
  ),
  [BillableProduct.MANAGED_CORO_AI_ESSENTIALS]: bundleKey(
    SubscriptionBundle.MANAGED_CORO_AI_ESSENTIALS
  ),
  [BillableProduct.MANAGED_CORO_AI_LITE]: bundleKey(SubscriptionBundle.MANAGED_CORO_AI_LITE),
  [BillableProduct.CORO_CLASSIC]: bundleKey(SubscriptionBundle.CORO_CLASSIC),
  [BillableProduct.MANAGED_CORO_CLASSIC]: bundleKey(SubscriptionBundle.MANAGED_CORO_CLASSIC),
  [BillableProduct.CORO_COMPLETE]: bundleKey(SubscriptionBundle.CORO_COMPLETE),
  [BillableProduct.CORO_ESSENTIALS]: bundleKey(SubscriptionBundle.CORO_ESSENTIALS),
  [BillableProduct.EMAIL_PROTECTION]: bundleKey(SubscriptionBundle.EMAIL_PROTECTION),
  [BillableProduct.ENDPOINT_PROTECTION]: bundleKey(SubscriptionBundle.ENDPOINT_PROTECTION),
  [BillableProduct.SASE]: bundleKey(SubscriptionBundle.SASE),
  [BillableProduct.CORO_MANAGED_COMPLETE]: bundleKey(SubscriptionBundle.MANAGED_CORO_COMPLETE),
  [BillableProduct.MANAGED_CORO_ESSENTIALS]: bundleKey(SubscriptionBundle.MANAGED_CORO_ESSENTIALS),
  [BillableProduct.MANAGED_EMAIL_PROTECTION]: bundleKey(
    SubscriptionBundle.MANAGED_EMAIL_PROTECTION
  ),
  [BillableProduct.MANAGED_ENDPOINT_PROTECTION]: bundleKey(
    SubscriptionBundle.MANAGED_ENDPOINT_PROTECTION
  ),
  [BillableProduct.MANAGED_SASE]: bundleKey(SubscriptionBundle.MANAGED_SASE),
  [BillableProduct.CLOUD_SECURITY]: moduleKey(SubscriptionModule.CLOUD_SECURITY),
  [BillableProduct.EDR]: moduleKey(SubscriptionModule.EDR),
  [BillableProduct.EMAIL_SECURITY]: moduleKey(SubscriptionModule.EMAIL_SECURITY),
  [BillableProduct.ENDPOINT_DATA_GOVERNANCE]: moduleKey(
    SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
  ),
  [BillableProduct.ENDPOINT_SECURITY]: moduleKey(SubscriptionModule.ENDPOINT_SECURITY),
  [BillableProduct.MDM]: moduleKey(SubscriptionModule.MDM),
  [BillableProduct.NETWORK]: moduleKey(SubscriptionModule.NETWORK),
  [BillableProduct.SECURED_WEB_GATEWAY]: moduleKey(SubscriptionModule.SWG),
  [BillableProduct.SECURITY_AWARENESS_TRAINING]: moduleKey(
    SubscriptionModule.SECURITY_AWARENESS_TRAINING
  ),
  [BillableProduct.USER_DATA_GOVERNANCE]: moduleKey(SubscriptionModule.USER_DATA_GOVERNANCE),
  [BillableProduct.BACKUP_AND_RESTORE]: moduleKey(SubscriptionModule.BACKUP_AND_RESTORE),
  [BillableProduct.SOC_CLOUD_SECURITY]: addonKey(SubscriptionAddon.SOC_CLOUD),
  [BillableProduct.SOC_EMAIL_SECURITY]: addonKey(SubscriptionAddon.SOC_EMAIL),
  [BillableProduct.SOC_ENDPOINT_SECURITY]: addonKey(SubscriptionAddon.SOC_ENDPOINT),
  [BillableProduct.SOC_ENDPOINT_DATA_GOVERNANCE]: addonKey(
    SubscriptionAddon.SOC_ENDPOINT_DATA_GOVERNANCE
  ),
  [BillableProduct.SOC_NETWORK]: addonKey(SubscriptionAddon.SOC_NETWORK),
  [BillableProduct.SOC_USER_DATA_GOVERNANCE]: addonKey(SubscriptionAddon.SOC_USER_DATA_GOVERNANCE),
  [BillableProduct.MDR]: addonKey(SubscriptionAddon.MDR),
  [BillableProduct.MANAGED_CORO]: customKey("managedCoro"),
  [BillableProduct.NFR]: customKey("nfr"),
  [BillableProduct.BILLING_EXCEPTION_OVERRIDE]: customKey("customProduct"),
  [BillableProduct.RAMP_COMMITMENT_ADJUSTMENT]: customKey("rampCommitmentAdjustment"),
};
